import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CalendarTodayOutlined as CalendarIcon,
  Category as CategoryIcon,
  EventBusy as BlockerIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';


const CalendarSettings = () => {
  const { t } = useTranslation();
  
  // State für Modalitäten
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [modalitySearch, setModalitySearch] = useState('');
  
  // State für Dienste
  const [services, setServices] = useState([]);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    shortCode: '',
    name: '',
    startTime: '08:00',
    endTime: '17:00',
    color: '#4FC3F7'
  });
  
  // State für Blocker
  const [blockers, setBlockers] = useState([]);
  const [blockerDialogOpen, setBlockerDialogOpen] = useState(false);
  const [editingBlocker, setEditingBlocker] = useState(null);
  const [blockerForm, setBlockerForm] = useState({
    shortCode: '',
    name: '',
    color: '#FFB74D'
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Notification states
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load initial data
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await loadCalendarSettings();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const loadCalendarSettings = async (retryCount = 0, maxRetries = 2) => {
    setLoading(true);
    try {
      // Wait for token to be available, especially on first load
      let token = sessionStorage.getItem('token');
      if (!token && retryCount === 0) {

        await new Promise(resolve => setTimeout(resolve, 500));
        token = sessionStorage.getItem('token');
      }
      
      const timestamp = new Date().toISOString();
      console.log(`🔑 [${timestamp}] Token:`, token ? `Token gefunden (Länge: ${token.length})` : 'KEIN TOKEN!');
      console.log(`🌐 [${timestamp}] API URL:`, `${getApiBaseUrl()}/admin/calendar-settings`);
      console.log(`🧪 [${timestamp}] Attempt ${retryCount + 1}/${maxRetries + 1}: Sende Request...`);
      
      if (!token) {
        console.warn(`⚠️ [${timestamp}] No token available, skipping API call`);
        return;
      }
      
      const response = await apiFetch('/admin/calendar-settings');
      

      
      if (response.ok) {
        const data = await response.json();

        
        // Convert string codes to objects with code and name
        const convertedModalities = (data.selectedModalities || []).map(code => {
          const modality = allModalities.find(m => m.code === code);
          return modality || { code, name: t(`admin.reportingManagement.calendarSettings.modalities.dicom.${code}`) };
        });
        
        setSelectedModalities(convertedModalities);
        setServices(data.services || []);
        setBlockers(data.blockers || []);
      } else if (response.status === 401 && retryCount < maxRetries) {
        const errorText = await response.text();
        console.warn(`🔄 [${timestamp}] 401 Error (attempt ${retryCount + 1}), retrying in 1s:`, errorText);
        setLoading(false);
        setTimeout(() => loadCalendarSettings(retryCount + 1, maxRetries), 1000);
        return;
      } else {
        const errorText = await response.text();
        console.error(`❌ [${timestamp}] API Error:`, response.status, errorText);
      }
    } catch (error) {
      console.error('Error loading calendar settings:', error);
      if (retryCount < maxRetries) {

        setTimeout(() => loadCalendarSettings(retryCount + 1, maxRetries), 1000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/calendar-settings`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectedModalities: selectedModalities.map(mod => mod.code)
        })
      });
      
      if (response.ok) {
        console.log('Settings saved successfully');
        setSuccessMessage(t('admin.reportingManagement.calendarSettings.saveSuccess'));
      } else {
        const errorText = await response.text();
        console.error('Error saving settings:', errorText);
        setError(t('admin.reportingManagement.calendarSettings.saveError'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(t('admin.reportingManagement.calendarSettings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // DICOM Modalitäten - Standard oben, Rest alphabetisch
  const standardModalities = [
    { code: 'DX', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.DX') },
    { code: 'CT', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.CT') },
    { code: 'MR', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.MR') },
    { code: 'RF', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.RF') },
    { code: 'XA', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.XA') },
    { code: 'MG', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.MG') },
    { code: 'US', name: t('admin.reportingManagement.calendarSettings.modalities.dicom.US') }
  ];

  const otherModalityCodes = [
    'AR', 'ASMT', 'AU', 'BDUS', 'BI', 'BMD', 'CR', 'CTPROTOCOL', 'DG', 'DOC', 'ECG', 'EPS', 'ES', 'FID', 'GM', 'HC', 'HD', 
    'IO', 'IOL', 'IVOCT', 'IVUS', 'KER', 'KO', 'LEN', 'LS', 'M3D', 'NM', 'OAM', 'OCT', 'OP', 'OPM', 'OPT', 'OPTBSV', 
    'OPTENF', 'OPV', 'OSS', 'OT', 'PLAN', 'PR', 'PT', 'PX', 'REG', 'RESP', 'RG', 'RTDOSE', 'RTIMAGE', 'RTINTENT', 
    'RTPLAN', 'RTRAD', 'RTRECORD', 'RTSEGANN', 'RTSTRUCT', 'RWV', 'SEG', 'SM', 'SMR', 'SR', 'SRF', 'STAIN', 'TG', 'VA', 'XC'
  ];

  const otherModalities = otherModalityCodes.map(code => ({
    code,
    name: t(`admin.reportingManagement.calendarSettings.modalities.dicom.${code}`)
  })).sort((a, b) => a.name.localeCompare(b.name));

  const allModalities = [...standardModalities, ...otherModalities];

  // Pastellfarben für Color-Picker - basierend auf dem bereitgestellten Farbschema
  const pastelColors = [
    // Rosa/Pink Töne (Reihe 1)
    '#F4C2C2', '#F2D7D5', '#F1C6C2', '#F0AEAE', '#FACBAD',
    // Orange/Pfirsich Töne (Reihe 2) 
    '#FFCCB6', '#F5DEB3', '#F0E68C', '#F5F5A0', '#F5F5DC',
    // Grün Töne (Reihe 3)
    '#C5D4A3', '#B8B8AA', '#C8E6C9', '#DCEDC1', '#E1E8B8',
    // Türkis/Mint Töne (Reihe 4)
    '#E8F5E8', '#E0F2F1', '#80CBC4', '#81C784', '#C5CAE9',
    // Blau Töne (Reihe 5)
    '#9FC5E8', '#B39DDB', '#BBDEFB', '#CFD8DC', '#E1E1E1',
    // Lila/Violett Töne (Reihe 6)
    '#F3E5F5', '#D1C4E9', '#CE93D8', '#BA68C8', '#DDA0DD'
  ];

  const handleServiceSubmit = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const url = editingService !== null 
        ? `${getApiBaseUrl()}/admin/calendar-services/${services[editingService].id}`
        : `${getApiBaseUrl()}/admin/calendar-services`;
      const method = editingService !== null ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serviceForm)
      });
      
      if (response.ok) {
        loadCalendarSettings(); // Reload data
        setServiceDialogOpen(false);
        setEditingService(null);
        setServiceForm({ shortCode: '', name: '', startTime: '08:00', endTime: '17:00', color: '#4FC3F7' });
      }
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const handleBlockerSubmit = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const url = editingBlocker !== null 
        ? `${getApiBaseUrl()}/admin/calendar-blockers/${blockers[editingBlocker].id}`
        : `${getApiBaseUrl()}/admin/calendar-blockers`;
      const method = editingBlocker !== null ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(blockerForm)
      });
      
      if (response.ok) {
        loadCalendarSettings(); // Reload data
        setBlockerDialogOpen(false);
        setEditingBlocker(null);
        setBlockerForm({ shortCode: '', name: '', color: '#FFB74D' });
      }
    } catch (error) {
      console.error('Error saving blocker:', error);
    }
  };

  const openServiceDialog = (index = null) => {
    if (index !== null) {
      setServiceForm(services[index]);
      setEditingService(index);
    }
    setServiceDialogOpen(true);
  };

  const openBlockerDialog = (index = null) => {
    if (index !== null) {
      setBlockerForm(blockers[index]);
      setEditingBlocker(index);
    }
    setBlockerDialogOpen(true);
  };

  const deleteService = async (index) => {
    try {
      const token = sessionStorage.getItem('token');
      const service = services[index];
      
      const response = await fetch(`${getApiBaseUrl()}/admin/calendar-services/${service.id}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        loadCalendarSettings(); // Reload data
      }
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const deleteBlocker = async (index) => {
    try {
      const token = sessionStorage.getItem('token');
      const blocker = blockers[index];
      
      const response = await fetch(`${getApiBaseUrl()}/admin/calendar-blockers/${blocker.id}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        loadCalendarSettings(); // Reload data
      }
    } catch (error) {
      console.error('Error deleting blocker:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarIcon color="primary" />
        {t('admin.reportingManagement.calendarSettings.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.reportingManagement.calendarSettings.description')}
      </Typography>

      {/* Notification Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Modalitäten Container */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TuneIcon color="primary" />
          <Typography variant="h6">
            {t('admin.reportingManagement.calendarSettings.modalities.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.reportingManagement.calendarSettings.modalities.description')}
        </Typography>
        
        {/* Ausgewählte Modalitäten oberhalb der Suchleiste */}
        {selectedModalities.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Ausgewählte Modalitäten ({selectedModalities.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedModalities.map((option, index) => (
                <Chip
                  key={option.code}
                  variant="outlined"
                  label={`${option.code} - ${option.name}`}
                  onDelete={() => {
                    const newSelected = selectedModalities.filter((_, i) => i !== index);
                    setSelectedModalities(newSelected);
                  }}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
        
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={allModalities}
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          value={selectedModalities}
          onChange={(event, newValue) => {
            // Duplikate entfernen basierend auf dem code
            const uniqueModalities = newValue.filter((item, index, self) => 
              index === self.findIndex(mod => mod.code === item.code)
            );
            setSelectedModalities(uniqueModalities);
          }}
          ListboxProps={{
            style: {
              maxHeight: '190px'
            }
          }}
          renderTags={() => null} // Keine Tags in der Suchleiste
          renderOption={(props, option) => {
            const isSelected = selectedModalities.some(selected => selected.code === option.code);
            return (
              <li 
                {...props} 
                key={option.code}
                style={{
                  ...props.style,
                  border: isSelected ? '2px solid var(--rd-primary)' : 'none',
                  borderRadius: isSelected ? '4px' : '0px',
                  margin: isSelected ? '2px' : '0px',
                  backgroundColor: isSelected ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'transparent'
                }}
              >
                <strong>{option.code}</strong>&nbsp;-&nbsp;{option.name}
                {isSelected && (
                  <span style={{ 
                    marginLeft: 'auto', 
                    color: 'var(--rd-primary)', 
                    fontWeight: 'bold' 
                  }}>
                    ✓
                  </span>
                )}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('admin.reportingManagement.calendarSettings.modalities.search')}
              placeholder={t('admin.reportingManagement.calendarSettings.modalities.searchPlaceholder')}
            />
          )}
        />
      </Paper>

      {/* Dienste Container */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" />
            <Typography variant="h6">
              {t('admin.reportingManagement.calendarSettings.services.title')}
            </Typography>
          </Box>
          <Button
            className="button"
            startIcon={<AddIcon />}
            onClick={() => openServiceDialog()}
          >
            {t('admin.reportingManagement.calendarSettings.services.addService')}
          </Button>
        </Box>
        
        <List>
          {services.map((service, index) => (
            <ListItem key={service.id || index}>
              <Avatar sx={{ bgcolor: service.color, mr: 2 }}>
                {service.shortCode}
              </Avatar>
              <ListItemText
                primary={service.name}
                secondary={`${service.startTime} - ${service.endTime}`}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => openServiceDialog(index)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => deleteService(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {services.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              {t('admin.reportingManagement.calendarSettings.services.noServices')}
            </Typography>
          )}
        </List>
      </Paper>

      {/* Blocker Container */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BlockerIcon color="primary" />
            <Typography variant="h6">
              {t('admin.reportingManagement.calendarSettings.blockers.title')}
            </Typography>
          </Box>
          <Button
            className="button"
            startIcon={<AddIcon />}
            onClick={() => openBlockerDialog()}
          >
            {t('admin.reportingManagement.calendarSettings.blockers.addBlocker')}
          </Button>
        </Box>
        
        <List>
          {blockers.map((blocker, index) => (
            <ListItem key={blocker.id || index}>
              <Avatar sx={{ bgcolor: blocker.color, mr: 2 }}>
                {blocker.shortCode}
              </Avatar>
              <ListItemText
                primary={blocker.name}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => openBlockerDialog(index)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => deleteBlocker(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {blockers.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              {t('admin.reportingManagement.calendarSettings.blockers.noBlockers')}
            </Typography>
          )}
        </List>
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          className="button"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </Box>

      {/* Service Dialog */}
      <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingService !== null 
            ? t('admin.reportingManagement.calendarSettings.services.editService') 
            : t('admin.reportingManagement.calendarSettings.services.addService')
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('admin.reportingManagement.calendarSettings.services.shortCode')}
                value={serviceForm.shortCode}
                onChange={(e) => setServiceForm({ ...serviceForm, shortCode: e.target.value })}
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('admin.reportingManagement.calendarSettings.services.name')}
                value={serviceForm.name}
                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label={t('admin.reportingManagement.calendarSettings.services.startTime')}
                value={serviceForm.startTime}
                onChange={(e) => setServiceForm({ ...serviceForm, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label={t('admin.reportingManagement.calendarSettings.services.endTime')}
                value={serviceForm.endTime}
                onChange={(e) => setServiceForm({ ...serviceForm, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin.reportingManagement.calendarSettings.services.color')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pastelColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setServiceForm({ ...serviceForm, color })}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: color,
                      border: serviceForm.color === color ? '3px solid var(--rd-text-primary)' : '1px solid var(--rd-border)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            className="button-cancel"
            onClick={() => setServiceDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            className="button"
            onClick={handleServiceSubmit}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocker Dialog */}
      <Dialog open={blockerDialogOpen} onClose={() => setBlockerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBlocker !== null 
            ? t('admin.reportingManagement.calendarSettings.blockers.editBlocker') 
            : t('admin.reportingManagement.calendarSettings.blockers.addBlocker')
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('admin.reportingManagement.calendarSettings.blockers.shortCode')}
                value={blockerForm.shortCode}
                onChange={(e) => setBlockerForm({ ...blockerForm, shortCode: e.target.value })}
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('admin.reportingManagement.calendarSettings.blockers.name')}
                value={blockerForm.name}
                onChange={(e) => setBlockerForm({ ...blockerForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin.reportingManagement.calendarSettings.blockers.color')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pastelColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setBlockerForm({ ...blockerForm, color })}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: color,
                      border: blockerForm.color === color ? '3px solid var(--rd-text-primary)' : '1px solid var(--rd-border)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            className="button-cancel"
            onClick={() => setBlockerDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            className="button"
            onClick={handleBlockerSubmit}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarSettings; 